import { Timeline, Avatar, Skeleton, Empty, Tag } from 'antd';
import { UserOutlined, PlayCircleOutlined, ShopOutlined, CrownOutlined, ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { formatCurrency } from '../../utils/format';

export const BidHistoryViewer = ({ bidHistory, loading, currentUser, lot, auctionOwnerId, rejectedBids = [] }) => {
    if (loading) {
        // Show skeleton loading for recent bids (3-5 items)
        return (
            <div style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: '8px' }}>
                <Timeline
                    mode="left"
                    items={[...Array(3)].map((_, index) => ({
                        color: 'gray',
                        children: (
                            <div style={{
                                background: '#fafafa',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: '1px solid #e8e8e8',
                                marginBottom: '8px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                            <Skeleton.Avatar size={32} active />
                                            <div style={{ flex: 1 }}>
                                                <Skeleton.Input 
                                                    active 
                                                    size="small" 
                                                    style={{ width: 140, height: 16 }} 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right', minWidth: 100 }}>
                                        <Skeleton.Input 
                                            active 
                                            size="small" 
                                            style={{ width: 90, height: 20, marginBottom: 4, marginLeft: 'auto' }} 
                                        />
                                        <Skeleton.Input 
                                            active 
                                            size="small" 
                                            style={{ width: 70, height: 12, marginBottom: 2, marginLeft: 'auto' }} 
                                        />
                                        <Skeleton.Input 
                                            active 
                                            size="small" 
                                            style={{ width: 70, height: 12, marginLeft: 'auto' }} 
                                        />
                                    </div>
                                </div>
                            </div>
                        )
                    }))}
                />
            </div>
        );
    }

    if (!bidHistory || bidHistory.length === 0) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                    <span style={{ color: '#8c8c8c' }}>No bids yet</span>
                }
                style={{ padding: '40px 20px' }}
            />
        );
    }

    // Combine regular bids and rejected bids, then sort by timestamp
    const allBids = [
        ...bidHistory.map(bid => ({ ...bid, type: 'accepted' })),
        ...rejectedBids.map(rejected => ({ 
            ...rejected, 
            type: 'rejected',
            created_at: rejected.timestamp,
            is_winning: false
        }))
    ].sort((a, b) => new Date(b.created_at || b.timestamp) - new Date(a.created_at || a.timestamp));

    return (
        <div style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: '8px' }}>
            <Timeline
                mode="left"
                items={allBids.map((bid, index) => {
                    // Handle rejected bids
                    if (bid.type === 'rejected') {
                        const isCurrentUser = currentUser && bid.user && Number(bid.user.id) === Number(currentUser.id);
                        return {
                            color: 'red',
                            children: (
                                <div style={{
                                    background: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
                                    padding: '14px 18px',
                                    borderRadius: '10px',
                                    border: '2px dashed #faad14',
                                    marginBottom: '10px',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    opacity: 0.8
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                <Avatar 
                                                    size={32}
                                                    icon={<UserOutlined />}
                                                    style={{
                                                        backgroundColor: '#faad14',
                                                        border: '2px solid #ffc53d'
                                                    }}
                                                >
                                                    {bid.user?.first_name?.[0]?.toUpperCase() || 'U'}
                                                </Avatar>
                                                <div>
                                                    <div style={{ 
                                                        fontWeight: 600, 
                                                        fontSize: '14px',
                                                        color: '#fa8c16',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                    }}>
                                                        {bid.user?.first_name} {bid.user?.last_name}
                                                        <span style={{ 
                                                            fontSize: '10px', 
                                                            background: '#ff4d4f', 
                                                            color: 'white', 
                                                            padding: '2px 6px', 
                                                            borderRadius: '10px',
                                                            fontWeight: 'bold'
                                                        }}>
                                                            REJECTED
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ 
                                                fontSize: '18px', 
                                                fontWeight: 700, 
                                                color: '#fa8c16',
                                                marginBottom: '4px',
                                                textDecoration: 'line-through'
                                            }}>
                                                {formatCurrency(bid.amount)}
                                            </div>
                                            {bid.minimumBid && (
                                                <div style={{ 
                                                    fontSize: '12px', 
                                                    color: '#52c41a',
                                                    fontWeight: 600,
                                                    marginBottom: '4px'
                                                }}>
                                                    Min: {formatCurrency(bid.minimumBid)}
                                                </div>
                                            )}
                                            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                                {moment(bid.timestamp).format('MMM DD, YYYY')}
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                                {moment(bid.timestamp).format('hh:mm:ss A')}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{
                                        padding: '8px 12px',
                                        background: 'rgba(255, 77, 79, 0.1)',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(255, 77, 79, 0.2)',
                                        marginTop: '8px'
                                    }}>
                                        <div style={{
                                            fontSize: '11px',
                                            color: '#ff4d4f',
                                            textAlign: 'center',
                                            fontWeight: 500
                                        }}>
                                            {bid.errorMessage || 'Bid was rejected'}
                                        </div>
                                    </div>
                                </div>
                            )
                        };
                    }
                    
                    // Regular bid handling (existing code)
                    // Check if this is a start bid or vendor bid
                    const isAuctionOwnerBid = bid.user_id && auctionOwnerId && Number(bid.user_id) === Number(auctionOwnerId);
                    // Don't show as winning if it's an auction owner bid
                    const isWinning = bid.is_winning && !isAuctionOwnerBid;
                    const isCurrentUser = currentUser && bid.user && Number(bid.user.id) === Number(currentUser.id);
                    const isLatest = index === 0;
                    const lotStatus = lot?.status;
                    const isClosed = lotStatus === 'closed';
                    const isCancelled = lotStatus === 'cancelled';
                    
                    const isStartBid = bid.is_start_bid || false;
                    const isVendorBid = bid.is_vendor_bid || false;
                    
                    // Different styling for "Sold to Other" (closed lot won by someone else)
                    if (isClosed && isWinning && !isCurrentUser) {
                        const winnerName = `${bid.user?.first_name || ''} ${bid.user?.last_name || ''}`.trim();
                        const isAuctionOwner = currentUser?.id && auctionOwnerId && Number(currentUser.id) === Number(auctionOwnerId);
                        
                        // Use blue styling for auction owners, red for others
                        const cardStyle = isAuctionOwner ? {
                            background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
                            border: '2px solid #1890ff',
                            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.2)'
                        } : {
                            background: 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)',
                            border: '2px solid #ff4d4f',
                            boxShadow: '0 4px 12px rgba(255, 77, 79, 0.2)'
                        };
                        
                        const topBarColor = isAuctionOwner 
                            ? 'linear-gradient(90deg, #1890ff, #40a9ff, #1890ff)'
                            : 'linear-gradient(90deg, #ff4d4f, #ff7875, #ff4d4f)';
                        
                        const avatarStyle = isAuctionOwner ? {
                            backgroundColor: '#1890ff',
                            border: '2px solid #40a9ff'
                        } : {
                            backgroundColor: '#ff4d4f',
                            border: '2px solid #ff7875'
                        };
                        
                        const textColor = isAuctionOwner ? '#1890ff' : '#ff4d4f';
                        const secondaryColor = isAuctionOwner ? '#40a9ff' : '#ff7875';
                        
                        return {
                            color: isAuctionOwner ? 'blue' : 'red',
                            children: (
                                <div style={{
                                    ...cardStyle,
                                    padding: '14px 18px',
                                    borderRadius: '10px',
                                    marginBottom: '10px',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: topBarColor,
                                        backgroundSize: '200% 100%',
                                        animation: 'shimmer 2s infinite'
                                    }} />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                <Avatar 
                                                    size={32}
                                                    icon={<UserOutlined />}
                                                    style={avatarStyle}
                                                >
                                                    {bid.user?.first_name?.[0]?.toUpperCase() || 'U'}
                                                </Avatar>
                                                <div>
                                                    <div style={{ 
                                                        fontWeight: 600, 
                                                        fontSize: '14px',
                                                        color: textColor,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        flexWrap: 'wrap'
                                                    }}>
                                                        {bid.user?.first_name} {bid.user?.last_name}
                                                        {bid.is_start_bid && (
                                                            <Tag 
                                                                color="blue"
                                                                icon={<PlayCircleOutlined />}
                                                                style={{
                                                                    margin: 0,
                                                                    fontSize: '9px',
                                                                    padding: '1px 6px',
                                                                    fontWeight: 600,
                                                                    borderRadius: '4px'
                                                                }}
                                                            >
                                                                Started Lot
                                                            </Tag>
                                                        )}
                                                        {bid.is_vendor_bid && (
                                                            <Tag 
                                                                color="purple"
                                                                icon={<ShopOutlined />}
                                                                style={{
                                                                    margin: 0,
                                                                    fontSize: '9px',
                                                                    padding: '1px 6px',
                                                                    fontWeight: 600,
                                                                    borderRadius: '4px'
                                                                }}
                                                            >
                                                                Vendor Bid
                                                            </Tag>
                                                        )}
                                                    </div>
                                                    {isLatest && (
                                                        <div style={{ 
                                                            fontSize: '10px', 
                                                            color: '#8c8c8c', 
                                                            marginTop: '2px',
                                                            fontStyle: 'italic'
                                                        }}>
                                                            Last bid
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ 
                                                fontSize: '18px', 
                                                fontWeight: 700, 
                                                color: textColor,
                                                marginBottom: '4px'
                                            }}>
                                                {formatCurrency(bid.amount)}
                                            </div>
                                            <div style={{ fontSize: '11px', color: secondaryColor }}>
                                                {moment(bid.created_at).format('MMM DD, YYYY')}
                                            </div>
                                            <div style={{ fontSize: '11px', color: secondaryColor }}>
                                                {moment(bid.created_at).format('hh:mm:ss A')}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{
                                        padding: '8px 12px',
                                        background: isAuctionOwner 
                                            ? 'rgba(24, 144, 255, 0.1)'
                                            : 'rgba(255, 77, 79, 0.1)',
                                        borderRadius: '6px',
                                        border: isAuctionOwner
                                            ? '1px solid rgba(24, 144, 255, 0.2)'
                                            : '1px solid rgba(255, 77, 79, 0.2)',
                                        marginTop: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px'
                                    }}>
                                        <div style={{
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            color: textColor,
                                            textTransform: 'uppercase',
                                            letterSpacing: '1px',
                                            padding: '4px 12px',
                                            background: isAuctionOwner
                                                ? 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)'
                                                : 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)',
                                            border: `2px dashed ${textColor}`,
                                            borderRadius: '4px',
                                            transform: 'rotate(-2deg)',
                                            boxShadow: isAuctionOwner
                                                ? '0 2px 4px rgba(24, 144, 255, 0.2)'
                                                : '0 2px 4px rgba(255, 77, 79, 0.2)',
                                            fontFamily: 'serif'
                                        }}>
                                            Sold To {winnerName}
                                        </div>
                                    </div>
                                </div>
                            )
                        };
                    }
                    
                    // Different styling for cancelled lots
                    if (isCancelled && isWinning) {
                        return {
                            color: 'purple',
                            children: (
                                <div style={{
                                    background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
                                    padding: '14px 18px',
                                    borderRadius: '10px',
                                    border: '2px solid #722ed1',
                                    marginBottom: '10px',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 4px 12px rgba(114, 46, 209, 0.2)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: 'linear-gradient(90deg, #722ed1, #9254de, #722ed1)',
                                        backgroundSize: '200% 100%',
                                        animation: 'shimmer 2s infinite'
                                    }} />
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                <Avatar 
                                                    size={32}
                                                    icon={<UserOutlined />}
                                                    style={{
                                                        backgroundColor: '#722ed1',
                                                        border: '2px solid #b37feb'
                                                    }}
                                                >
                                                    {bid.user?.first_name?.[0]?.toUpperCase() || 'U'}
                                                </Avatar>
                                                <div>
                                                    <div style={{ 
                                                        fontWeight: 600, 
                                                        fontSize: '14px',
                                                        color: '#722ed1',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        flexWrap: 'wrap'
                                                    }}>
                                                        {!isCurrentUser && `${bid.user?.first_name || ''} ${bid.user?.last_name || ''}`.trim()}
                                                        {isCurrentUser && (
                                                            <Tag color="blue" style={{ 
                                                                margin: 0, 
                                                                fontSize: '14px', 
                                                                padding: '4px 12px', 
                                                                fontWeight: 600,
                                                                borderRadius: '6px',
                                                                border: 'none',
                                                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                                                boxShadow: '0 2px 4px rgba(24, 144, 255, 0.3)',
                                                                color: 'white'
                                                            }}>
                                                                You
                                                            </Tag>
                                                        )}
                                                        {bid.is_start_bid && (
                                                            <Tag 
                                                                color="blue"
                                                                icon={<PlayCircleOutlined />}
                                                                style={{
                                                                    margin: 0,
                                                                    fontSize: '9px',
                                                                    padding: '1px 6px',
                                                                    fontWeight: 600,
                                                                    borderRadius: '4px'
                                                                }}
                                                            >
                                                                Started Lot
                                                            </Tag>
                                                        )}
                                                        {bid.is_vendor_bid && (
                                                            <Tag 
                                                                color="purple"
                                                                icon={<ShopOutlined />}
                                                                style={{
                                                                    margin: 0,
                                                                    fontSize: '9px',
                                                                    padding: '1px 6px',
                                                                    fontWeight: 600,
                                                                    borderRadius: '4px'
                                                                }}
                                                            >
                                                                Vendor Bid
                                                            </Tag>
                                                        )}
                                                    </div>
                                                    {isLatest && (
                                                        <div style={{ 
                                                            fontSize: '10px', 
                                                            color: '#8c8c8c', 
                                                            marginTop: '2px',
                                                            fontStyle: 'italic'
                                                        }}>
                                                            Last bid
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ 
                                                fontSize: '18px', 
                                                fontWeight: 700, 
                                                color: '#722ed1',
                                                marginBottom: '4px'
                                            }}>
                                                {formatCurrency(bid.amount)}
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#9254de' }}>
                                                {moment(bid.created_at).format('MMM DD, YYYY')}
                                            </div>
                                            <div style={{ fontSize: '11px', color: '#9254de' }}>
                                                {moment(bid.created_at).format('hh:mm:ss A')}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{
                                        padding: '8px 12px',
                                        background: 'rgba(114, 46, 209, 0.1)',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(114, 46, 209, 0.2)',
                                        marginTop: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px'
                                    }}>
                                        {/* <Tag 
                                            icon={<CrownOutlined />} 
                                            color="purple"
                                            style={{ margin: 0, fontSize: '11px', padding: '2px 8px', fontWeight: 600 }}
                                        >
                                            Cancelled - Reserve Not Met
                                        </Tag> */}
                                        <div style={{
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            color: '#722ed1',
                                            textTransform: 'uppercase',
                                            letterSpacing: '1px',
                                            padding: '4px 12px',
                                            background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
                                            border: '2px dashed #722ed1',
                                            borderRadius: '4px',
                                            transform: 'rotate(-2deg)',
                                            boxShadow: '0 2px 4px rgba(114, 46, 209, 0.2)',
                                            fontFamily: 'serif'
                                        }}>
                                            Cancelled - Reserve Not Met
                                        </div>
                                    </div>
                                </div>
                            )
                        };
                    }
                    
                    return {
                        color: isWinning ? 'green' : isCurrentUser ? 'blue' : 'gray',
                        children: (
                            <div 
                                style={{
                                    background: isWinning 
                                        ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
                                        : isCurrentUser
                                            ? 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)'
                                            : 'linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)',
                                    padding: '16px 18px',
                                    borderRadius: '12px',
                                    border: isWinning 
                                        ? '2px solid #52c41a'
                                        : isCurrentUser
                                            ? '2px solid #1890ff'
                                            : '1px solid #e8e8e8',
                                    marginBottom: '12px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: isLatest ? 'scale(1.02)' : 'scale(1)',
                                    boxShadow: isWinning
                                        ? '0 6px 20px rgba(82, 196, 26, 0.25), 0 2px 8px rgba(82, 196, 26, 0.15)'
                                        : isLatest 
                                            ? '0 6px 20px rgba(24, 144, 255, 0.2), 0 2px 8px rgba(24, 144, 255, 0.1)'
                                            : '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
                                    animation: isWinning ? 'winningPulse 2s ease-in-out infinite' : undefined,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    cursor: 'default'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isLatest) {
                                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.01)';
                                        e.currentTarget.style.boxShadow = isWinning
                                            ? '0 8px 24px rgba(82, 196, 26, 0.3), 0 4px 12px rgba(82, 196, 26, 0.2)'
                                            : isCurrentUser
                                                ? '0 8px 24px rgba(24, 144, 255, 0.25), 0 4px 12px rgba(24, 144, 255, 0.15)'
                                                : '0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isLatest) {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.style.boxShadow = isWinning
                                            ? '0 6px 20px rgba(82, 196, 26, 0.25), 0 2px 8px rgba(82, 196, 26, 0.15)'
                                            : isCurrentUser
                                                ? '0 6px 20px rgba(24, 144, 255, 0.2), 0 2px 8px rgba(24, 144, 255, 0.1)'
                                                : '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)';
                                    }
                                }}
                            >
                                {isWinning && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'linear-gradient(90deg, transparent, rgba(82, 196, 26, 0.1), transparent)',
                                        animation: 'shimmer 2s infinite',
                                        pointerEvents: 'none'
                                    }} />
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                                            <Avatar 
                                                size={40}
                                                icon={<UserOutlined />}
                                                style={{
                                                    backgroundColor: isWinning ? '#52c41a' : isCurrentUser ? '#1890ff' : '#d9d9d9',
                                                    border: isWinning 
                                                        ? '3px solid #b7eb8f' 
                                                        : isCurrentUser 
                                                            ? '3px solid #91d5ff' 
                                                            : '2px solid #f0f0f0',
                                                    boxShadow: isWinning
                                                        ? '0 2px 8px rgba(82, 196, 26, 0.3)'
                                                        : isCurrentUser
                                                            ? '0 2px 8px rgba(24, 144, 255, 0.3)'
                                                            : '0 1px 4px rgba(0, 0, 0, 0.1)',
                                                    fontSize: '16px',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {bid.user?.first_name?.[0]?.toUpperCase() || 'U'}
                                            </Avatar>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ 
                                                    fontWeight: 600, 
                                                    fontSize: '15px',
                                                    color: isWinning ? '#389e0d' : isCurrentUser ? '#0958d9' : '#262626',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    marginBottom: '2px'
                                                }}>
                                                    {!isCurrentUser && `${bid.user?.first_name || ''} ${bid.user?.last_name || ''}`.trim()}
                                                    {isCurrentUser && (
                                                        <Tag 
                                                            color="blue" 
                                                            style={{ 
                                                                margin: 0, 
                                                                fontSize: '12px', 
                                                                padding: '4px 10px', 
                                                                fontWeight: 600,
                                                                borderRadius: '6px',
                                                                border: 'none',
                                                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                                                boxShadow: '0 2px 4px rgba(24, 144, 255, 0.3)',
                                                                color: 'white'
                                                            }}
                                                        >
                                                            You
                                                        </Tag>
                                                    )}
                                                    {isStartBid && (
                                                        <Tag 
                                                            color="blue"
                                                            icon={<PlayCircleOutlined />}
                                                            style={{
                                                                margin: 0,
                                                                fontSize: '10px',
                                                                padding: '2px 8px',
                                                                fontWeight: 600,
                                                                borderRadius: '4px'
                                                            }}
                                                        >
                                                            Started Lot
                                                        </Tag>
                                                    )}
                                                    {isVendorBid && (
                                                        <Tag 
                                                            color="purple"
                                                            icon={<ShopOutlined />}
                                                            style={{
                                                                margin: 0,
                                                                fontSize: '10px',
                                                                padding: '2px 8px',
                                                                fontWeight: 600,
                                                                borderRadius: '4px'
                                                            }}
                                                        >
                                                            Vendor Bid
                                                        </Tag>
                                                    )}
                                                    {isLatest && !isStartBid && !isVendorBid && (
                                                        <Tag 
                                                            color="gold"
                                                            style={{
                                                                margin: 0,
                                                                fontSize: '10px',
                                                                padding: '2px 8px',
                                                                fontWeight: 600,
                                                                borderRadius: '4px',
                                                                marginLeft: 'auto'
                                                            }}
                                                        >
                                                            Latest
                                                        </Tag>
                                                    )}
                                                </div>
                                                {isLatest && (
                                                    <div style={{ 
                                                        fontSize: '11px', 
                                                        color: '#8c8c8c', 
                                                        marginTop: '2px',
                                                        fontStyle: 'italic',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                        <ClockCircleOutlined style={{ fontSize: '10px' }} />
                                                        Last bid
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right', minWidth: '120px' }}>
                                        <div style={{ 
                                            fontSize: '20px', 
                                            fontWeight: 700, 
                                            color: isWinning ? '#389e0d' : isCurrentUser ? '#0958d9' : '#595959',
                                            marginBottom: '6px',
                                            background: isWinning 
                                                ? 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)'
                                                : isCurrentUser
                                                    ? 'linear-gradient(135deg, #1890ff 0%, #0958d9 100%)'
                                                    : 'none',
                                            WebkitBackgroundClip: isWinning || isCurrentUser ? 'text' : 'none',
                                            WebkitTextFillColor: isWinning || isCurrentUser ? 'transparent' : '#595959',
                                            backgroundClip: isWinning || isCurrentUser ? 'text' : 'none',
                                            textShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                                        }}>
                                            {formatCurrency(bid.amount)}
                                        </div>
                                        <div style={{ 
                                            fontSize: '11px', 
                                            color: '#8c8c8c',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            gap: '4px',
                                            marginBottom: '2px'
                                        }}>
                                            <ClockCircleOutlined style={{ fontSize: '10px' }} />
                                            {moment(bid.created_at).format('MMM DD, YYYY')}
                                        </div>
                                        <div style={{ 
                                            fontSize: '11px', 
                                            color: '#8c8c8c',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            gap: '4px'
                                        }}>
                                            {moment(bid.created_at).format('hh:mm:ss A')}
                                        </div>
                                    </div>
                                </div>
                                {isWinning && (
                                    <div style={{
                                        padding: '8px 12px',
                                        background: isClosed 
                                            ? 'rgba(82, 196, 26, 0.1)'
                                            : 'rgba(250, 173, 20, 0.1)',
                                        borderRadius: '6px',
                                        border: isClosed
                                            ? '1px solid rgba(82, 196, 26, 0.2)'
                                            : '1px solid rgba(250, 173, 20, 0.2)',
                                        marginTop: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px'
                                    }}>
                                        {/* {isClosed && isCurrentUser ? null : (
                                            <Tag 
                                                icon={<CrownOutlined />} 
                                                color={isClosed ? "green" : "gold"}
                                                style={{ margin: 0, fontSize: '11px', padding: '2px 8px', fontWeight: 600 }}
                                            >
                                                {isClosed ? 'Winner' : 'Winning'}
                                            </Tag>
                                        )} */}
                                        {!isClosed && (
                                            <Tag 
                                                icon={<CrownOutlined />} 
                                                color="gold"
                                                style={{ margin: 0, fontSize: '11px', padding: '2px 8px', fontWeight: 600 }}
                                            >
                                                Winning
                                            </Tag>
                                        )}
                                        {isClosed && isCurrentUser && (
                                            <div style={{
                                                fontSize: '12px',
                                                fontWeight: 700,
                                                color: '#52c41a',
                                                textTransform: 'uppercase',
                                                letterSpacing: '1px',
                                                padding: '4px 12px',
                                                background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                                                border: '2px dashed #52c41a',
                                                borderRadius: '4px',
                                                transform: 'rotate(-2deg)',
                                                boxShadow: '0 2px 4px rgba(82, 196, 26, 0.2)',
                                                fontFamily: 'serif'
                                            }}>
                                                ✓ Sold to You
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    };
                })}
            />
        </div>
    );
};